import * as React from 'react';
import { useLocalStorage } from './useLocalStorage';

interface IMeetingLogEntry {
	status: 'logged' | 'noShow';
	timestamp: number;
}

type MeetingLogStorage = Record<string, IMeetingLogEntry>;

const STORAGE_KEY = 'meeting-notes-log-status';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
const DATE_KEY_SEPARATOR = '::';

/**
 * Normalizes a meeting start time into a YYYY-MM-DD key segment. Returns null when no usable date is
 * available, in which case callers fall back to keying by meeting id alone.
 */
export function getMeetingLogDateKey(meetingStartTime?: string | null): string | null {
	if (!meetingStartTime) return null;

	const isoDate = /^(\d{4}-\d{2}-\d{2})/.exec(meetingStartTime);
	if (isoDate) return isoDate[1];

	const parsed = new Date(meetingStartTime);
	if (Number.isNaN(parsed.getTime())) return null;

	return parsed.toISOString().slice(0, 10);
}

/**
 * Google and Outlook reuse the same calendar event id when a meeting is rescheduled, so a status keyed
 * by id alone follows the event onto its new date. Pairing the id with the occurrence date keeps each
 * occurrence independent. Meetings without a start time keep the id-only key.
 */
export function getMeetingLogKey(meetingId: string, meetingStartTime?: string | null): string {
	const dateKey = getMeetingLogDateKey(meetingStartTime);
	return dateKey ? `${meetingId}${DATE_KEY_SEPARATOR}${dateKey}` : meetingId;
}

/**
 * Hook that persists meeting log status to localStorage with 30-day TTL expiration.
 * This allows users to see which meetings they've logged or marked as no-show
 * even after refreshing the page or closing the browser.
 */
export function useMeetingLogStorage() {
	const [meetingLogs, setMeetingLogs] = useLocalStorage<MeetingLogStorage>({
		key: STORAGE_KEY,
		initialValue: {},
	});

	// Clean up expired entries on mount only.
	// Why empty deps are correct here:
	// 1. STORAGE_KEY is a constant - the key never changes
	// 2. Adding meetingLogs to deps would cause an infinite loop (cleanup updates state → triggers effect)
	// 3. Expired entries are also lazily filtered in isMeetingLogged/isMeetingNoShow, so users never see stale data
	// 4. Running cleanup only on mount is sufficient since TTL is 30 days - no need to re-run during a session
	React.useEffect(() => {
		const now = Date.now();
		const entries = Object.entries(meetingLogs || {});
		const validEntries = entries.filter(([, entry]) => now - entry.timestamp < TTL_MS);

		// Only update if we removed some entries
		if (validEntries.length < entries.length) {
			setMeetingLogs(Object.fromEntries(validEntries));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only on mount (see comment above)
	}, []);

	const getActiveEntry = (meetingId: string, meetingStartTime?: string | null): IMeetingLogEntry | undefined => {
		const key = getMeetingLogKey(meetingId, meetingStartTime);
		// Entries written before statuses were date-scoped are keyed by id alone; honor them so a status set
		// by the previous version isn't lost. They drain out on their own once the TTL elapses.
		const entry = meetingLogs?.[key] ?? (key === meetingId ? undefined : meetingLogs?.[meetingId]);
		if (!entry) return undefined;

		if (Date.now() - entry.timestamp >= TTL_MS) return undefined;

		return entry;
	};

	const isMeetingLogged = (meetingId: string, meetingStartTime?: string | null): boolean => {
		return getActiveEntry(meetingId, meetingStartTime)?.status === 'logged';
	};

	const isMeetingNoShow = (meetingId: string, meetingStartTime?: string | null): boolean => {
		return getActiveEntry(meetingId, meetingStartTime)?.status === 'noShow';
	};

	const setMeetingStatus = (
		meetingId: string,
		meetingStartTime: string | null | undefined,
		status: IMeetingLogEntry['status']
	): void => {
		const key = getMeetingLogKey(meetingId, meetingStartTime);
		setMeetingLogs(prevLogs => ({
			...prevLogs,
			[key]: {
				status,
				timestamp: Date.now(),
			},
		}));
	};

	const markMeetingLogged = (meetingId: string, meetingStartTime?: string | null): void => {
		setMeetingStatus(meetingId, meetingStartTime, 'logged');
	};

	const markMeetingNoShow = (meetingId: string, meetingStartTime?: string | null): void => {
		setMeetingStatus(meetingId, meetingStartTime, 'noShow');
	};

	return {
		isMeetingLogged,
		isMeetingNoShow,
		markMeetingLogged,
		markMeetingNoShow,
	};
}
